import { ReactNode, useCallback, useMemo } from 'react';

import { CwfActiveDocument, CwfDocumentType } from '@/api';
import { useStudyGuard } from '@/shared/guard';

import { usePreviousStudies } from '../../../../hook';
import { mapStudyToStudyResult } from '../../../../util';
import { PatientDocument } from '../patient-docs-side-panel.model';
import { PatientDocsContext } from './patient-docs.context';
import { PatientDocsState } from './patient-docs.model';
import { generatePatientDocsByCarePlanId, generatePatientDocsByStudyId } from './patient-docs.util';

interface PatientDocsProviderProps {
  children: ReactNode;
  documents: CwfActiveDocument[];
  onUpdateSidePanelPatientDoc: (value: PatientDocument) => void;
  patientDocument?: PatientDocument;
  types: CwfDocumentType[];
}

export function PatientDocsProvider({
  children,
  documents,
  onUpdateSidePanelPatientDoc,
  patientDocument,
  types,
}: PatientDocsProviderProps) {
  const {
    radiologistScan: { poetryMetadata, scanDate, skuID },
    studyId,
  } = useStudyGuard();

  const { currentStudy, previousStudyResults } = usePreviousStudies();

  const previousStudyDocs = useMemo(
    () =>
      Object.entries(
        generatePatientDocsByStudyId({
          docs: documents,
          studies: previousStudyResults,
          studyId,
          types,
        }),
      ),
    [documents, previousStudyResults, studyId, types],
  );

  const currentStudyDocs = useMemo(
    () =>
      poetryMetadata?.carePlanId === undefined
        ? Object.values(
            generatePatientDocsByStudyId({
              docs: documents.filter((doc) => doc.studyId === studyId),
              studies: currentStudy ? [mapStudyToStudyResult(currentStudy)] : [],
              studyId,
              types,
            }),
          )[0]
        : generatePatientDocsByCarePlanId({
            carePlanId: poetryMetadata?.carePlanId,
            docs: documents,
            poetryMetadata: poetryMetadata ?? undefined,
            scanDate,
            servicesInfo: poetryMetadata?.servicesInfo ?? [],
            sku: skuID,
            studyId,
            timezone: currentStudy?.location?.timezone,
            types,
          }),
    [documents, currentStudy, studyId, types, poetryMetadata, skuID, scanDate],
  );

  const handleUpdatePatientDocument = useCallback(
    (doc: PatientDocument) => onUpdateSidePanelPatientDoc(doc),
    [onUpdateSidePanelPatientDoc],
  );

  const value: PatientDocsState = useMemo(
    () => ({
      currentStudyDocs,
      onUpdatePatientDocument: handleUpdatePatientDocument,
      patientDocument,
      previousStudyDocs,
      types,
    }),
    [currentStudyDocs, patientDocument, previousStudyDocs, types, handleUpdatePatientDocument],
  );

  return <PatientDocsContext value={value}>{children}</PatientDocsContext>;
}
